<template>
  <v-dialog v-model="showDialog" max-width="600px" scrollable persistent :fullscreen="$vuetify.display.xs" @close="close">
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>{{ dialogTitle }}</span>
        <v-spacer />
        <v-btn icon variant="text" size="small" @click="close" :disabled="submitting"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>

        <survey-question v-if="currentQuestion === 0" :back-button-text="null" :next-disabled="!answers.bestAccountabilityApp" @on-next="currentQuestion = 1">
          <yes-no :question="`Is ${appName} the best parental monitoring/accountability app you've used?`" v-model="answers.bestAccountabilityApp" />
        </survey-question>

        <survey-question
            v-else-if="currentQuestion === 1 && answers.bestAccountabilityApp === 'yes'"
            :next-disabled="false"
            next-button-text="Submit"
            @on-back="currentQuestion = 0"
            :next-loading="submitting"
            @on-next="submit">
          <div class="mt-3">
            <div class="text-h6 mb-2"><v-icon color="accent" class="mr-1">star</v-icon>A 5-star rating goes a long way</div>
            <div class="mb-4">
              We only get about 10 reviews per month — every one counts.
              <a class="font-italic ml-1 text-caption text-accent" style="cursor: pointer;" @click="showLearnWhy = !showLearnWhy">{{ showLearnWhy ? '(close)' : '(learn why lower ratings hurt)'}}</a>
            </div>

            <v-expand-transition>
              <v-alert v-if="showLearnWhy" variant="tonal" type="info" density="compact" class="mb-4">
                App ratings position {{ appName }} above or below competitors in search results.
                Most people only install the top 1-2 apps, so positioning matters.
                A 5 star rating means the app is better than the alternatives — it does not require perfection.
                4 stars or lower pushes other apps above {{ appName }}.
              </v-alert>
            </v-expand-transition>
            <v-btn
                href="https://play.google.com/store/apps/details?id=com.camhart.netcountable"
                @click="answers.clickedLeaveRating = true"
                target="_blank"
                color="primary"
                variant="outlined"
                :disabled="submitting"
                class="mb-2 text-none"
                block>
              <v-icon class="mr-2" size="small" color="accent">shop</v-icon>
              Google Play — Truple
            </v-btn>
            <v-btn
                href="https://apps.apple.com/app/id6753020993?action=write-review"
                @click="answers.clickedLeaveRating = true"
                target="_blank"
                color="primary"
                variant="outlined"
                :disabled="submitting"
                class="mb-2 text-none"
                block>
              <v-icon class="mr-2" size="small" color="accent">smartphone</v-icon>
              App Store — Best Accountability
            </v-btn>
            <v-btn
                href="https://apps.apple.com/app/id6747992475?action=write-review"
                @click="answers.clickedLeaveRating = true"
                target="_blank"
                color="primary"
                variant="outlined"
                :disabled="submitting"
                class="mb-2 text-none"
                block>
              <v-icon class="mr-2" size="small" color="accent">smartphone</v-icon>
              App Store — ScreenKin
            </v-btn>
            <div class="text-caption text-medium-emphasis font-italic mt-1">
              Leaving a rating is optional, but greatly appreciated.
              <a style="cursor: pointer;" @click="showAlreadyLeftOne = true">Already left one?</a>
            </div>
            <v-dialog v-model="showAlreadyLeftOne" width="400px">
              <v-card>
                <v-card-title class="d-flex align-center">
                  <span>Thank you!</span>
                  <v-spacer />
                  <v-btn icon variant="text" size="small" @click="showAlreadyLeftOne = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                  <div class="mb-3">If it's been a while, consider updating your review. Google and Apple weigh recent reviews more heavily.</div>
                  <div>Otherwise just submit the survey and know that we're grateful for your support.</div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn variant="elevated" @click="showAlreadyLeftOne = false" color="primary">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </survey-question>

        <survey-question v-else-if="currentQuestion === 1 && answers.bestAccountabilityApp === 'no'"
                         :next-disabled="!answers.improveDescription_freetext || submitting"
                         next-button-text="Submit"
                         @on-back="currentQuestion = 0"
                         :next-loading="submitting"
                         @on-next="submit">
          <long-text :question="`How can we improve ${appName} so it is the best?`" v-model="answers.improveDescription_freetext" :autofocus="true" />
        </survey-question>

        <survey-question v-if="currentQuestion === 10" :back-button-text="null" next-button-text="Close" @on-next="close" :next-disabled="submitting" :next-loading="submitting">
          <div class="mt-3">
            <div class="text-h6 mb-2">Thanks for the feedback!</div>
            <div class="text-body-2">
              We ask for feedback every 3 months. We know it can be a bit much, but hearing from you helps us understand how well we're serving you. Thank you for taking the time.
            </div>
          </div>
        </survey-question>

      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>

import YesNo from "@/components/surveys/questions/YesNo";
import SurveyQuestion from "@/components/surveys/SurveyQuestion";
import LongText from "@/components/surveys/questions/LongText";
import SurveyApi from "@/aws/survey/SurveyApi";
import StoreConstants from "@/StoreConstants";

export default {
  name: "SurveyFeedback",
  components: {LongText, SurveyQuestion, YesNo},
  props: {
    modelValue: {}
  },
  data() {
    return {
      appName: process.env.VUE_APP_NAME,
      showLearnWhy: false,
      showAlreadyLeftOne: false,
      currentQuestion: 0,
      showDialog: this.modelValue,
      submitting: false,
      answers: {
        improveDescription_freetext: '',
        clickedLeaveRating: false,
        bestAccountabilityApp: null
      }
    }
  },
  computed: {
    dialogTitle() {
      if (this.currentQuestion === 10) return 'Thank You'
      if (this.currentQuestion === 1 && this.answers.bestAccountabilityApp === 'yes') return 'Leave a Rating'
      if (this.currentQuestion === 1 && this.answers.bestAccountabilityApp === 'no') return 'Your Feedback'
      return 'Feedback Survey'
    }
  },
  methods: {
    async close() {
      this.submitting = true
      await this.$store.dispatch(StoreConstants.actions.loadAccount)
      this.submitting = false
      this.showDialog = false
      this.$emit('update:modelValue', false)
      this.$emit('on-close')
    },
    async submit() {
      try {
        this.submitting = true
        const surveyCategory = 'feedback'
        await SurveyApi.submitSurveyResults(surveyCategory, this.answers)
        this.currentQuestion = 10
      } catch(e) {
        // eslint-disable-next-line no-console
        console.log(e)
        this.$swal('An error occurred.')
        throw e
      } finally {
        this.submitting = false
      }
    }
  },
  watch: {
    modelValue: function(newVal) {
      this.showDialog = newVal
    }
  }
}
</script>

<style scoped>

</style>
